import { DateRange, TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Card, CardHeader, CardTitle, CardContent } from "@corpuscomplete/web-ui/components/card";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { NotFoundPage } from "@corpuscomplete/web/components/not-found-page";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { MealPlan } from "@corpuscomplete/core/nutrition/meal-plan";
import { MealPlanAgentPlanningChat } from "@corpuscomplete/web/modules/nutrition/meal-plan/components/agent-chat";
import type { FoodItemMatcher } from "@corpuscomplete/core/nutrition/food-matcher";
import { createId, NIL_UUID } from "@corpuscomplete/core/utils";
import { FoodItem, FoodItemInputView } from "@corpuscomplete/web/components/nutrition/food-item";
import { Food } from "@corpuscomplete/core/nutrition/food";
import {
  Item,
  ItemContent,
  ItemTitle,
  ItemDescription,
  ItemActions,
  ItemMedia,
} from "@corpuscomplete/web-ui/components/item";
import { CircleHelpIcon, PlusIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import {
  Page,
  PageBody,
  PageContent,
  PageHeader,
  PageSidePanel,
} from "@corpuscomplete/web/components/layout";
import { PanelRightIcon } from "lucide-react";
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Textarea } from "@corpuscomplete/web-ui/components/textarea";
import { Separator } from "@corpuscomplete/web-ui/components/separator";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@corpuscomplete/web-ui/components/empty";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import {
  NutrientAmountsLabel,
  NutrientTargetsLabel,
} from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { MealPlanDetail } from "@corpuscomplete/core-ui/nutrition/meal-plan/detail";
import { SlotTargetsPopoverContent } from "@corpuscomplete/web/modules/nutrition/meal-plan/components/slot-targets-dialog";
import { SlotEditor } from "@corpuscomplete/web/modules/nutrition/meal-plan/components/slot-editor";
import { OccurrenceEditorPopoverContent } from "@corpuscomplete/web/modules/nutrition/meal-plan/components/occurrence-editor-dialog";
import { MoreHorizontalIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@corpuscomplete/web-ui/components/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@corpuscomplete/web-ui/components/tooltip";
import { FoodSearchCombobox } from "@corpuscomplete/web/components/nutrition/food-search-combobox";
import { FoodSearchDialog } from "@corpuscomplete/web/components/nutrition/food-search-dialog";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { VerticalDragReorderShellRow } from "@corpuscomplete/web/components/common/vertical-drag-reorder";
import { useVerticalDragReorder } from "@corpuscomplete/web/hooks/use-vertical-drag-reorder";

export function MealPlanPage({ id: mealPlanId }: { id: string }) {
  const { userId } = useAuth();

  return (
    <MealPlanDetail.Provider mealPlanId={mealPlanId} userId={userId}>
      <MealPlanPageContent />
    </MealPlanDetail.Provider>
  );
}

function MealPlanPageContent() {
  const [chatOpen, setChatOpen] = useState(false);
  const { userId } = useAuth();
  const mealPlan = MealPlanDetail.useMealPlan();
  const { session } = MealPlanDetail.useAgentSession();
  const { canEdit } = MealPlanDetail.useDomainConstraints();
  const { isLoading } = MealPlanDetail.useLoadingState();
  const { unavailabilityReason, isLoading: isNutritionContextLoading } =
    MealPlanDetail.useNutritionContext();

  if (isLoading || isNutritionContextLoading) {
    return null;
  }

  if (!mealPlan) {
    return <NotFoundPage />;
  }

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Text className="shrink-0 font-medium">Meal Plan</Text>
              <Text variant="sm" className="truncate text-muted-foreground">
                {DateRange.format(
                  { dateStart: mealPlan.dateStart, dateEnd: mealPlan.dateEnd },
                  { separator: " - " },
                )}
              </Text>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setChatOpen(!chatOpen)}>
              <PanelRightIcon />
            </Button>
          </div>
        </PageHeader>
        <PageContent className="px-4 py-4 sm:px-6">
          <MealPlanForm mealPlan={mealPlan} readOnly={mealPlan.status === "published"} />
        </PageContent>
      </PageBody>
      <PageSidePanel open={chatOpen} setOpen={setChatOpen}>
        {session ? (
          <MealPlanAgentPlanningChat
            userId={userId}
            agentVersion={session.agentVersion}
            sessionId={session.id}
            unavailabilityReason={unavailabilityReason}
            canEditSession={canEdit}
          />
        ) : (
          <MealPlanAgentSessionEmptyState />
        )}
      </PageSidePanel>
    </Page>
  );
}

function MealPlanAgentSessionEmptyState() {
  const { userId } = useAuth();
  const mealPlan = MealPlanDetail.useMealPlan();
  const { canCreateAgentSession } = MealPlanDetail.useDomainConstraints();
  const { unavailabilityReason } = MealPlanDetail.useNutritionContext();
  const { createAgentSession } = MealPlanDetail.useMutations();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const warning = MealPlanDetail.getChatUnavailabilityWarningContent(unavailabilityReason);

  if (!mealPlan) {
    return null;
  }

  const description = canCreateAgentSession
    ? "Manual edits stay available here even before the chat starts."
    : mealPlan.status === "published"
      ? "This meal plan needs to be a draft before an AI planning session can start."
      : mealPlan.ownerId === userId
        ? "An AI planning session is not available right now."
        : "Only the meal plan owner can start an AI planning session.";

  return (
    <div className="flex h-full flex-col">
      {warning && (
        <div className="mx-3 mt-3 flex items-center gap-2 rounded-md bg-muted px-2 py-1.5 text-muted-foreground">
          <TriangleAlertIcon className="size-3.5 shrink-0" />
          <Text variant="xs">{warning.message}</Text>
        </div>
      )}
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <PanelRightIcon className="size-8 text-muted-foreground" />
        <div className="space-y-1">
          <Text>Open an AI planning session for this meal plan.</Text>
          <Text variant="sm" className="text-muted-foreground">
            {description}
          </Text>
        </div>
        {createError && (
          <Text variant="sm" className="text-destructive">
            {createError}
          </Text>
        )}
        <Button
          disabled={!canCreateAgentSession || isCreating}
          onClick={async () => {
            if (!canCreateAgentSession || isCreating) {
              return;
            }

            setCreateError(null);
            setIsCreating(true);

            try {
              const mutation = createAgentSession();
              const clientResult = (await mutation.client) as
                | { type?: string; error?: { message?: string } }
                | undefined;

              if (clientResult?.type === "error") {
                setCreateError(clientResult.error?.message ?? "Failed to create agent session.");
                return;
              }
            } catch (error) {
              setCreateError(
                error instanceof Error ? error.message : "Failed to create agent session.",
              );
            } finally {
              setIsCreating(false);
            }
          }}
        >
          <Text variant="sm">
            {isCreating ? "Creating Meal Plan Agent Session..." : "Create Meal Plan Agent Session"}
          </Text>
        </Button>
      </div>
    </div>
  );
}

function MealPlanForm({
  mealPlan,
  readOnly = false,
}: {
  mealPlan: MealPlan.Info;
  readOnly?: boolean;
}) {
  const { canEdit, canPublish, canUnpublish } = MealPlanDetail.useDomainConstraints();
  const { addMealSlot, publish, unpublish } = MealPlanDetail.useMutations();
  const violations = MealPlanDetail.usePublishViolations();
  const [autoFocusMealId, setAutoFocusMealId] = useState<string | null>(null);
  const [blockedUnpublishOpen, setBlockedUnpublishOpen] = useState(false);
  const slotRefs = useRef(new Map<string, HTMLDivElement | null>());
  const mealRefs = useRef(new Map<string, HTMLDivElement | null>());
  const editable = !readOnly && canEdit;
  const unpublishBlocked = MealPlan.isUnpublishBlocked(mealPlan);
  const showBlockedUnpublish = canUnpublish && unpublishBlocked;

  const registerSlotRef = useCallback((slotId: string, element: HTMLDivElement | null) => {
    slotRefs.current.set(slotId, element);
  }, []);

  const registerMealRef = useCallback((mealId: string, element: HTMLDivElement | null) => {
    mealRefs.current.set(mealId, element);
  }, []);

  const handleFirstViolation = useCallback((target?: MealPlanDetail.PublishViolationTarget) => {
    if (!target) {
      return;
    }

    if (target.entity === "slot") {
      slotRefs.current.get(target.id)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }

    if (target.entity === "meal") {
      mealRefs.current.get(target.id)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, []);

  return (
    <div className="group/slots mx-auto flex w-full max-w-6xl flex-col gap-3 pb-8">
      {(canPublish || canUnpublish) && (
        <div className="flex justify-end">
          {canPublish ? (
            <div className="flex items-center gap-2">
              {violations !== null && (
                <Badge variant="destructive" className="gap-1.5 px-2 py-1">
                  <TriangleAlertIcon className="size-3.5 shrink-0" />
                  Fix publish blockers
                </Badge>
              )}
              <Button
                size="sm"
                onClick={() =>
                  void publish({
                    onError: handleFirstViolation,
                  })
                }
              >
                Publish Meal Plan
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {showBlockedUnpublish ? (
                <Popover open={blockedUnpublishOpen} onOpenChange={setBlockedUnpublishOpen}>
                  <PopoverTrigger
                    render={<Button size="sm" variant="outline" className="gap-1.5" />}
                    onClick={() => setBlockedUnpublishOpen((open) => !open)}
                  >
                    <TriangleAlertIcon className="size-3.5 text-destructive" />
                    Unpublish Meal Plan
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-80 p-3">
                    <Text variant="sm">{MealPlan.BLOCKED_UNPUBLISH_MESSAGE}</Text>
                  </PopoverContent>
                </Popover>
              ) : (
                <Button size="sm" variant="outline" onClick={unpublish}>
                  Unpublish Meal Plan
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {mealPlan.slots.length === 0 && (
        <Empty
          className={
            violations?.mealPlan.noSlots ? "border-destructive/40 bg-destructive/5" : "border"
          }
        >
          <EmptyHeader>
            <EmptyTitle>No slots yet</EmptyTitle>
            <EmptyDescription>
              Start by adding a slot for breakfast, lunch, dinner, or anything else you want to
              plan.
            </EmptyDescription>
          </EmptyHeader>
          {editable && (
            <div className="mt-2 flex justify-center">
              <InlineAddRow
                label="Add slot"
                alwaysVisible
                onClick={() =>
                  addMealSlot({
                    id: createId(),
                    name: "New Slot",
                    time: Temporal.PlainTime.from("12:00"),
                    userNutrientTargets: [],
                    meals: [],
                  })
                }
              />
            </div>
          )}
        </Empty>
      )}
      {mealPlan.slots.map((slot, index) => (
        <Fragment key={slot.id}>
          <MealPlanSlotSection
            slot={slot}
            autoFocusMealId={autoFocusMealId}
            onNameAutoFocused={(mealId) => {
              setAutoFocusMealId((current) => (current === mealId ? null : current));
            }}
            onAddMealRequested={(mealId) => setAutoFocusMealId(mealId)}
            registerSlotRef={registerSlotRef}
            registerMealRef={registerMealRef}
          />
          {index < mealPlan.slots.length - 1 && <Separator className="my-1" />}
        </Fragment>
      ))}
      {editable && mealPlan.slots.length > 0 && (
        <InlineAddRow
          label="Add slot"
          onClick={() =>
            addMealSlot({
              id: createId(),
              name: "New Slot",
              time: Temporal.PlainTime.from("12:00"),
              userNutrientTargets: [],
              meals: [],
            })
          }
        />
      )}
    </div>
  );
}

function MealPlanSlotSection({
  slot,
  autoFocusMealId,
  onNameAutoFocused,
  onAddMealRequested,
  registerSlotRef,
  registerMealRef,
}: {
  slot: MealPlan.Slot;
  autoFocusMealId: string | null;
  onNameAutoFocused: (mealId: string) => void;
  onAddMealRequested: (mealId: string) => void;
  registerSlotRef: (slotId: string, element: HTMLDivElement | null) => void;
  registerMealRef: (mealId: string, element: HTMLDivElement | null) => void;
}) {
  const { userId } = useAuth();
  const { canEdit } = MealPlanDetail.useDomainConstraints();
  const {
    addMeal,
    editMealSlot,
    removeMealSlot,
    setMealUserNutrientTargets,
    removeMealUserNutrientTargets,
  } = MealPlanDetail.useMutations();
  const { session } = MealPlanDetail.useAgentSession();
  const { byMealId: mealWorkflowExecutionsByMealId } =
    MealPlanDetail.useMealWorkflowExecutionsByMeal({
      agentSessionId: session?.id ?? NIL_UUID,
    });
  const { emptyName, noMeals } = MealPlanDetail.useSlotPublishViolations(slot.id);
  const editable = canEdit;
  const mergedMeals = MealPlanDetail.mergeMealsAndWorkflowStatusByMealId(
    slot.meals,
    mealWorkflowExecutionsByMealId,
  );
  const { slotTargets } = MealPlanDetail.buildSlotHeaderNutrients({
    userId,
    userNutrientTargets: slot.userNutrientTargets,
    mergedMeals,
  });

  const addEmptyMeal = () => {
    const id = createId();
    addMeal({
      id,
      groupId: id,
      mealSlotId: slot.id,
      userId,
      name: "",
      items: [],
      itemDescriptors: [],
      sortIndex: MealPlanDetail.getNextMealSortIndexInLane({
        meals: slot.meals,
        mealSlotId: slot.id,
        userId,
      }),
      occurrences: [{ id: createId(), date: null }],
    });
    onAddMealRequested(id);
  };

  return (
    <div
      ref={(element) => registerSlotRef(slot.id, element)}
      className="group/slot flex flex-col gap-3 py-1"
    >
      <div
        className={
          emptyName ? "rounded-xl border border-destructive/40 bg-destructive/5 p-3" : undefined
        }
      >
        {editable ? (
          <EditableSlotHeader
            slot={slot}
            targets={slotTargets}
            onEditSlot={(updates) => editMealSlot(slot.id, updates)}
            onEditTargets={(targets) =>
              targets
                ? setMealUserNutrientTargets({
                    userId,
                    mealSlotId: slot.id,
                    nutrientTargets: targets,
                  })
                : removeMealUserNutrientTargets({
                    userId,
                    mealSlotId: slot.id,
                  })
            }
            onRemoveSlot={() => removeMealSlot(slot.id)}
          />
        ) : (
          <div className="space-y-1 px-1">
            <Text variant="h3" className="font-semibold">
              {slot.name}
            </Text>
            <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
              <Text variant="sm" className="text-muted-foreground">
                {TemporalHelpers.formatTime(slot.time)}
              </Text>
              <Text variant="sm" className="text-muted-foreground">
                ·
              </Text>
              {slotTargets ? (
                <NutrientTargetsLabel targets={slotTargets} abbreviated textMuted />
              ) : (
                <Text variant="sm" className="text-muted-foreground">
                  No nutrient targets
                </Text>
              )}
            </div>
          </div>
        )}
        {emptyName && (
          <Text variant="sm" className="mt-2 px-1 text-destructive">
            Give this slot a name before publishing.
          </Text>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {mergedMeals.map((meal) => (
          <MealCard
            key={meal.id}
            meal={meal}
            readOnly={!editable}
            className="min-w-0"
            autoFocusName={autoFocusMealId === meal.id}
            onNameAutoFocused={() => onNameAutoFocused(meal.id)}
            registerMealRef={registerMealRef}
          />
        ))}
        {editable && mergedMeals.length > 0 && (
          <Button
            variant="ghost"
            className="hidden border text-muted-foreground md:flex md:opacity-0 md:transition-opacity md:group-hover/slot:opacity-100 md:group-focus-within/slot:opacity-100"
            onClick={addEmptyMeal}
          >
            <PlusIcon className="size-4" />
          </Button>
        )}
        {mergedMeals.length === 0 && (
          <div
            className={`rounded-xl border border-dashed px-4 py-6 text-center md:col-span-2 xl:col-span-3 ${noMeals ? "border-destructive/40 bg-destructive/5 text-destructive" : "bg-muted/10 text-muted-foreground"}`}
          >
            <Text variant="sm">No meals in this slot yet.</Text>
            {noMeals && (
              <Text variant="sm" className="mt-2 text-destructive">
                Add at least one meal before publishing.
              </Text>
            )}
            {editable && (
              <div className="mt-3 flex justify-center">
                <InlineAddRow label="Add meal" alwaysVisible onClick={addEmptyMeal} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MealCard({
  className,
  meal,
  readOnly,
  autoFocusName = false,
  onNameAutoFocused,
  registerMealRef,
}: {
  className?: string;
  meal: MealPlanDetail.MergedMeal;
  readOnly: boolean;
  autoFocusName?: boolean;
  onNameAutoFocused?: () => void;
  registerMealRef?: (mealId: string, element: HTMLDivElement | null) => void;
}) {
  const [name, setName] = useState(meal.name);
  const [occurrencePopoverOpen, setOccurrencePopoverOpen] = useState(false);
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [foodSearchOpen, setFoodSearchOpen] = useState(false);
  const foodSearchState = FoodSearchUI.useSearchState();
  const nameInputRef = useRef<HTMLTextAreaElement>(null);
  const { addMealItem, editMeal, removeMeal, removeMealItem, removeMealItemDescriptor } =
    MealPlanDetail.useMutations();
  const { emptyName, hasDescriptors, noItems } = MealPlanDetail.useMealPublishViolations(meal.id);
  const foodItems: Food.Item[] = [];
  for (const data of meal.items) {
    if (data.type === "item") {
      foodItems.push(data.item);
    }
  }

  const addSelectedFood = (selected: Food.Item) => {
    addMealItem(meal.id, {
      id: createId(),
      foodServingId: selected.serving.id,
      amount: 1,
      sortIndex: MealPlanDetail.getNextMealItemSortIndex(meal.items),
    });
  };

  const closeFoodSearch = () => {
    setFoodSearchOpen(false);
    foodSearchState.reset();
  };

  useEffect(() => {
    if (!autoFocusName || readOnly) {
      return;
    }

    nameInputRef.current?.focus();
    nameInputRef.current?.select();
    onNameAutoFocused?.();
  }, [autoFocusName, onNameAutoFocused, readOnly]);

  useLayoutEffect(() => {
    const element = nameInputRef.current;
    if (!element) {
      return;
    }

    element.style.height = "0px";

    const lineHeight = Number.parseFloat(window.getComputedStyle(element).lineHeight);
    const maxHeight = Number.isFinite(lineHeight) ? lineHeight * 2 + 4 : 44;
    element.style.height = `${Math.min(element.scrollHeight, maxHeight)}px`;
  }, [name]);

  return (
    <Card
      ref={(element) => registerMealRef?.(meal.id, element)}
      size="sm"
      className={`rounded-xl border bg-background shadow-none ${emptyName || noItems ? "border-destructive/40 bg-destructive/5" : ""} ${className ?? ""}`}
    >
      <CardHeader className="gap-3 border-b bg-transparent pb-3">
        <div className="flex items-start gap-2">
          <CardTitle className="min-w-0 flex-1">
            {readOnly ? (
              <Tooltip>
                <TooltipTrigger render={<div className="min-w-0" />}>
                  <Text className="line-clamp-2 text-sm font-medium">{meal.name}</Text>
                </TooltipTrigger>
                <TooltipContent>{meal.name}</TooltipContent>
              </Tooltip>
            ) : (
              <div className="relative min-w-0">
                <Textarea
                  ref={nameInputRef}
                  value={name}
                  title={meal.name}
                  variant="ghost"
                  textVariant="md"
                  rows={1}
                  placeholder="Meal name"
                  className={cn(
                    "min-h-0 max-h-11 overflow-hidden px-1 py-0.5 text-base font-medium leading-5 hover:bg-transparent focus:bg-transparent",
                    isNameFocused ? "caret-foreground" : "caret-transparent",
                  )}
                  onFocus={() => setIsNameFocused(true)}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={(event) => {
                    event.currentTarget.scrollTop = 0;
                    setIsNameFocused(false);
                    if (name !== meal.name && name.trim() !== "") {
                      editMeal(meal.id, { name: name.trim() });
                    }
                  }}
                />
                <div
                  aria-hidden="true"
                  title={meal.name}
                  className={cn(
                    "pointer-events-none absolute inset-0 w-full rounded-md bg-background px-1 py-0.5 text-left",
                    isNameFocused ? "invisible" : "visible",
                  )}
                >
                  <div
                    className={cn(
                      "line-clamp-2 whitespace-pre-wrap break-words text-base font-medium leading-5",
                      name ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {name || "Meal name"}
                  </div>
                </div>
              </div>
            )}
          </CardTitle>
        </div>
        {readOnly && meal.occurrenceCount > 1 ? (
          <Text variant="xs" className="text-muted-foreground">
            Planned {meal.occurrenceCount} times
          </Text>
        ) : null}
        {!readOnly && (
          <div className="flex items-center gap-1.5">
            <Popover open={occurrencePopoverOpen} onOpenChange={setOccurrencePopoverOpen}>
              <PopoverTrigger
                render={<Button variant="ghost" size="sm" className="text-muted-foreground" />}
              >
                <Text variant="xs">Planned {meal.occurrenceCount} times</Text>
              </PopoverTrigger>
              <OccurrenceEditorPopoverContent
                onOpenChange={setOccurrencePopoverOpen}
                mealId={meal.id}
                occurrences={meal.occurrences}
              />
            </Popover>
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
                <MoreHorizontalIcon className="size-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem variant="destructive" onClick={() => removeMeal(meal.id)}>
                  Remove Meal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {(emptyName || noItems || hasDescriptors) && (
          <div className="space-y-1">
            {emptyName && (
              <Text variant="xs" className="text-destructive">
                Give this meal a name before publishing.
              </Text>
            )}
            {noItems && (
              <Text variant="xs" className="text-destructive">
                Add at least one item before publishing.
              </Text>
            )}
            {hasDescriptors && (
              <Text variant="xs" className="text-destructive">
                Replace descriptor-only items before publishing.
              </Text>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-2.5">
        {!readOnly && (
          <div className="flex items-center justify-between gap-3">
            {foodItems.length > 0 || meal.hasDescriptors ? (
              <div className="text-muted-foreground">
                <NutrientAmountsLabel
                  items={foodItems}
                  abbreviated
                  hasIncompleteData={meal.hasDescriptors}
                />
              </div>
            ) : (
              <span />
            )}
            <Text variant="xs" className="text-muted-foreground">
              Search to add a food
            </Text>
          </div>
        )}
        {!readOnly && (
          <FoodSearchCombobox
            placeholder="Search to add a food"
            onSelect={addSelectedFood}
            onOpenAdvancedSearch={({ text }) => {
              foodSearchState.onChangeText(text);
              setFoodSearchOpen(true);
            }}
          />
        )}
        <FoodSearchDialog
          open={foodSearchOpen}
          onOpenChange={(open) => {
            if (open) {
              setFoodSearchOpen(true);
              return;
            }

            closeFoodSearch();
          }}
          searchState={foodSearchState}
          onSelectItem={(selected) => {
            addSelectedFood(selected);
            foodSearchState.onChangeText("");
          }}
          placeholder="Search to add a food"
        />
        {readOnly && (foodItems.length > 0 || meal.hasDescriptors) && (
          <div className="text-muted-foreground">
            <NutrientAmountsLabel
              items={foodItems}
              abbreviated
              hasIncompleteData={meal.hasDescriptors}
            />
          </div>
        )}
        {readOnly ? (
          <div className="flex flex-col">
            {meal.items.map((data) => (
              <MealItemRowContent
                key={data.id}
                data={data}
                readOnly
                highlightedDescriptors={hasDescriptors}
                rowClassName={getMealItemRowClassName({
                  data,
                  highlightedDescriptors: hasDescriptors,
                })}
              />
            ))}
          </div>
        ) : (
          <EditableMealItemsList
            meal={meal}
            highlightedDescriptors={hasDescriptors}
            onRemoveItem={removeMealItem}
            onRemoveDescriptor={removeMealItemDescriptor}
          />
        )}
      </CardContent>
    </Card>
  );
}

function EditableMealItemsList({
  meal,
  highlightedDescriptors,
  onRemoveItem,
  onRemoveDescriptor,
}: {
  meal: MealPlanDetail.MergedMeal;
  highlightedDescriptors: boolean;
  onRemoveItem: (mealId: string, itemId: string) => unknown;
  onRemoveDescriptor: (mealId: string, descriptorId: string) => unknown;
}) {
  const { editMealItem, reorderMealItem } = MealPlanDetail.useMutations();
  const settledItems = meal.items;
  const reorder = useVerticalDragReorder({
    items: settledItems,
    getItemId: (item) => item.id,
    getItemSignature: (item) => {
      if (item.type === "item") {
        return `${item.type}:${item.sortIndex}:${item.item.amount}:${item.item.serving.id}`;
      }

      return `${item.type}:${item.sortIndex}:${JSON.stringify(item.descriptor)}`;
    },
    onReorder: ({ activeId, dropTarget }) => {
      void reorderMealItem(meal.id, {
        activeId,
        dropTarget,
      });
    },
  });

  return (
    <DndContext {...reorder.dndContextProps}>
      <div className="flex flex-col">
        {settledItems.map((data) => {
          return (
            <VerticalDragReorderShellRow
              key={data.id}
              className={cn(
                "cursor-default",
                data.type === "descriptor" && highlightedDescriptors && "bg-destructive/5",
              )}
              {...reorder.getRowProps(data.id)}
            >
              <MealItemRowContent
                data={data}
                readOnly={false}
                highlightedDescriptors={highlightedDescriptors}
                rowClassName={getMealItemRowClassName({ data, highlightedDescriptors })}
                onEditItemAmount={(amount) => editMealItem(meal.id, data.id, { amount })}
                onEditItemServing={(serving) =>
                  editMealItem(meal.id, data.id, {
                    foodServingId: serving.id,
                    amount: serving.defaultAmount,
                  })
                }
                onRemoveItem={() => onRemoveItem(meal.id, data.id)}
                onRemoveDescriptor={() => onRemoveDescriptor(meal.id, data.id)}
              />
            </VerticalDragReorderShellRow>
          );
        })}
      </div>
      <DragOverlay dropAnimation={null}>
        {reorder.activeItem ? (
          <MealItemDragOverlay
            data={reorder.activeItem}
            width={reorder.overlayWidth}
            highlightedDescriptors={highlightedDescriptors}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function MealItemRowContent({
  data,
  readOnly,
  highlightedDescriptors,
  rowClassName,
  onEditItemAmount,
  onEditItemServing,
  onRemoveItem,
  onRemoveDescriptor,
}: {
  data: MealPlanDetail.MergedItem;
  readOnly: boolean;
  highlightedDescriptors: boolean;
  rowClassName?: string;
  onEditItemAmount?: (amount: number) => void;
  onEditItemServing?: (serving: Food.Serving) => void;
  onRemoveItem?: () => void;
  onRemoveDescriptor?: () => void;
}) {
  if (data.type === "item") {
    if (readOnly) {
      return (
        <div className={rowClassName}>
          <FoodItem item={data.item} titleLines={2} className="rounded-none py-2" />
        </div>
      );
    }

    return (
      <div className={rowClassName}>
        <FoodItemInputView
          className="flex-1 rounded-none py-2"
          itemInput={{ amount: data.item.amount, foodServingId: data.item.serving.id }}
          titleLines={2}
          onEditItemAmount={(amount) => onEditItemAmount?.(amount)}
          onEditItemServing={(serving) => onEditItemServing?.(serving)}
          onRemoveItem={() => onRemoveItem?.()}
        />
      </div>
    );
  }

  return (
    <div className={rowClassName}>
      <FoodItemDescriptorView
        descriptor={data.descriptor}
        highlighted={highlightedDescriptors}
        onRemove={readOnly ? undefined : onRemoveDescriptor}
      />
    </div>
  );
}

function MealItemDragOverlay({
  data,
  width,
  highlightedDescriptors,
}: {
  data: MealPlanDetail.MergedItem | null;
  width?: number;
  highlightedDescriptors: boolean;
}) {
  if (!data) {
    return null;
  }

  return (
    <div
      className="rounded-lg bg-background/95 shadow-lg ring-1 ring-border/80"
      style={width ? { width } : undefined}
    >
      <MealItemRowContent
        data={data}
        readOnly
        highlightedDescriptors={highlightedDescriptors}
        rowClassName={getMealItemRowClassName({
          data,
          highlightedDescriptors,
        })}
      />
    </div>
  );
}

function getMealItemRowClassName({
  data,
  highlightedDescriptors,
}: {
  data: MealPlanDetail.MergedItem;
  highlightedDescriptors: boolean;
}): string | undefined {
  return [data.type === "descriptor" && highlightedDescriptors ? "bg-destructive/5" : undefined]
    .filter(Boolean)
    .join(" ");
}

function EditableSlotHeader({
  slot,
  targets,
  onEditSlot,
  onEditTargets,
  onRemoveSlot,
}: {
  slot: MealPlan.Slot;
  targets: MealPlan.SlotUserNutrientTarget["nutrientTargets"] | null;
  onEditSlot: (updates: Partial<Pick<MealPlan.Slot, "name" | "time">>) => void;
  onEditTargets: (targets: MealPlan.SlotUserNutrientTarget["nutrientTargets"] | null) => void;
  onRemoveSlot: () => void;
}) {
  const { userId } = useAuth();
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [name, setName] = useState(slot.name);
  const [time, setTime] = useState(toTimeInput(slot.time));

  const userTargets =
    slot.userNutrientTargets.find((target) => target.userId === userId)?.nutrientTargets ?? null;

  return (
    <Popover open={targetsOpen} onOpenChange={setTargetsOpen}>
      <SlotEditor
        name={name}
        time={time}
        onChangeName={(nextName) => {
          setName(nextName);
          if (nextName.trim() !== "" && nextName !== slot.name) {
            onEditSlot({ name: nextName.trim() });
          }
        }}
        onChangeTime={(nextTime) => {
          setTime(nextTime);
          if (nextTime !== "" && nextTime !== toTimeInput(slot.time)) {
            onEditSlot({ time: Temporal.PlainTime.from(nextTime) });
          }
        }}
        targetsControl={
          <PopoverTrigger
            render={<Button variant="ghost" size="sm" className="text-muted-foreground" />}
          >
            {targets ? (
              <NutrientTargetsLabel targets={targets} abbreviated />
            ) : (
              <Text variant="xs" className="text-muted-foreground">
                No nutrient targets
              </Text>
            )}
          </PopoverTrigger>
        }
        onRemove={onRemoveSlot}
      />
      <SlotTargetsPopoverContent
        open={targetsOpen}
        onOpenChange={setTargetsOpen}
        initialTargets={userTargets}
        onSave={onEditTargets}
      />
    </Popover>
  );
}

function InlineAddRow({
  label,
  onClick,
  alwaysVisible = false,
}: {
  label: string;
  onClick: () => void;
  alwaysVisible?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={`w-full rounded-none border border-dashed border-border/80 text-muted-foreground transition-opacity hover:bg-muted/20 hover:text-foreground ${alwaysVisible ? "opacity-100" : "opacity-100 md:opacity-0 md:group-hover/slot:opacity-100 md:group-focus-within/slot:opacity-100 md:group-hover/slots:opacity-100 md:group-focus-within/slots:opacity-100"}`}
      onClick={onClick}
    >
      <PlusIcon className="size-4" />
      <Text variant="xs">{label}</Text>
    </Button>
  );
}

function toTimeInput(value: Temporal.PlainTime): string {
  const parts = value.toString().split(":");
  if (parts.length >= 2) {
    return `${parts[0] ?? "00"}:${parts[1] ?? "00"}`;
  }
  return value.toString();
}

export function FoodItemDescriptorView({
  descriptor,
  highlighted = false,
  onRemove,
}: {
  descriptor: FoodItemMatcher.ItemDescriptor;
  highlighted?: boolean;
  onRemove?: () => void;
}) {
  const quantityStr = descriptor.quantity !== null ? `${descriptor.quantity} ` : "";
  const unitStr = descriptor.unit !== null ? `${descriptor.unit}` : "";
  const amountLabel = `${quantityStr}${unitStr}`.trim();

  return (
    <Item
      variant="muted"
      size="xs"
      className="rounded-none border-transparent bg-transparent py-2 shadow-none"
    >
      <ItemContent>
        <div className="flex min-w-0 items-start gap-2.5">
          <ItemMedia
            variant="image"
            className="mt-0.5 size-7 rounded-none bg-muted text-muted-foreground [&_svg]:size-4"
          >
            <CircleHelpIcon className="size-4" />
          </ItemMedia>
          <div className="min-w-0 flex-1">
            <ItemTitle>{descriptor.name}</ItemTitle>
            <ItemDescription className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
              {amountLabel ? <span>{amountLabel}</span> : null}
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex" />}>
                  <Badge variant={highlighted ? "destructive" : "outline"}>Needs match</Badge>
                </TooltipTrigger>
                <TooltipContent>
                  This item still needs to be matched to a food before the meal plan can be
                  published.
                </TooltipContent>
              </Tooltip>
            </ItemDescription>
          </div>
        </div>
      </ItemContent>
      <ItemActions className="gap-1 self-start pt-0.5">
        {onRemove ? (
          <Button
            data-no-drag
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onRemove}
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <XIcon className="size-4" />
          </Button>
        ) : null}
      </ItemActions>
    </Item>
  );
}
